import React, {
	Dispatch,
	SetStateAction,
	useEffect,
	useState,
	useRef,
} from 'react';
import { useApi } from '../../../../../hooks/useApi';
import {
	useQuoteDispatch,
	useQuoteState,
} from '../../../../../stateProviders/QuoteStateProvider';
import {
	Stack,
	Typography,
	Box,
	TextField,
	IconButton,
	Button,
	Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { QuoteState } from 'src/types/stateProviders/QuoteStateProvider';
import AddIcon from '@mui/icons-material/Add';
import AccessorySelection from './Accessories/AccessorySelection';
import CloseIcon from '@mui/icons-material/Close';
import { DeleteAccessoryModal } from './Accessories/DeleteAccessoryModal';
import { useAdaptiveDropdown } from '../../../../../hooks/useAdaptiveDropdown';
import Accessory from 'src/types/shared/accessory';
import AccessoryCard from './Accessories/AccessoryCard';
import AppAutocomplete from 'src/globalComponents/AppAutoComplete';
import { DECKING_TYPE_ORDER } from '../../../../../constants/deckingTypeOrder';

export const DeckStyle = () => {
	const api = useApi();
	const quoteState = useQuoteState();
	const quoteDispatch = useQuoteDispatch();
	const [drinkRailBrands, setDrinkRailBrands] = useState([]);
	const [showAddAccessoryModal, setShowAddAccessoryModal] = useState<{
		status: boolean;
		optionIndex: number | null;
	}>({ status: false, optionIndex: null });
	const [showDeleteAccessoryModal, setShowDeleteAccessoryModal] = useState<{
		accessory: { id: string; name: string; quantity: number } | null;
		status: boolean;
		optionIndex: number | null;
	}>({ accessory: null, status: false, optionIndex: null });
	const [panelKey, setPanelKey] = useState(Date.now());

	const [accessories, setAccessories] = useState<Accessory[]>([]);

	useEffect(() => {
		(async () => {
			const result = await api.accessory.getAccessories();
			setAccessories(result.rows || []);
		})();
	}, []);

	// Refs and state for adaptive dropdown (3 options x 3 dropdowns each = 9 total)
	const deckingTypeRefs = [
		useRef<HTMLDivElement>(null),
		useRef<HTMLDivElement>(null),
		useRef<HTMLDivElement>(null),
	];
	const railingTypeRefs = [
		useRef<HTMLDivElement>(null),
		useRef<HTMLDivElement>(null),
		useRef<HTMLDivElement>(null),
	];
	const railCapStyleRefs = [
		useRef<HTMLDivElement>(null),
		useRef<HTMLDivElement>(null),
		useRef<HTMLDivElement>(null),
	];

	const [deckingTypeOpen, setDeckingTypeOpen] = useState([false, false, false]);
	const [railingTypeOpen, setRailingTypeOpen] = useState([false, false, false]);
	const [railCapStyleOpen, setRailCapStyleOpen] = useState([
		false,
		false,
		false,
	]);

	useEffect(() => {
		(async () => {
			const deckingTypes = await api.limiter.get({ name: 'Brand' });
			const railingTypes = await api.limiter.get({ name: 'Railing Type' });
			const railCapStyles = await api.limiter.get({ name: 'Rail Cap Style' });
			const drinkRailBrands = await api.limiter.get({
				name: 'Drink Rail Brands',
			});

			setDrinkRailBrands(drinkRailBrands.rows[0].values);

			quoteDispatch({
				deckingTypes: deckingTypes.rows[0].values
					.filter((type) => type !== 'N/A')
					.sort((a, b) => {
						const ai = DECKING_TYPE_ORDER.indexOf(a);
						const bi = DECKING_TYPE_ORDER.indexOf(b);
						if (ai === -1 && bi === -1) return a.localeCompare(b);
						if (ai === -1) return 1;
						if (bi === -1) return -1;
						return ai - bi;
					}),
				railingTypes: railingTypes.rows[0].values,
				railCapStyles: railCapStyles.rows[0].values.filter(
					(type) => type !== 'N/A'
				),
			});
		})();
	}, []);

	const handleOptionSelect = async (
		type: string,
		value: string | null,
		index: number
	) => {
		const updatedOptions = [...quoteState.options];

		if (type === 'deckingType' && value === null) {
			updatedOptions[index] = {
				deckingType: null,
				railingType: null,
				railCapStyle: null,
				accessories: [],
			};
		} else if (type === 'railingType' && value === null) {
			updatedOptions[index] = {
				...updatedOptions[index],
				railingType: null,
				railCapStyle: null,
			};
		} else {
			// Reset railCapStyle if deckingType changes and Drink Rail is not available
			if (
				type === 'deckingType' &&
				updatedOptions[index].railCapStyle === 'Drink Rail' &&
				!drinkRailBrands.includes(value)
			) {
				updatedOptions[index].railCapStyle = null;
			}

			// Reset railCapStyle if railingType changes and is not IRX
			if (type === 'railingType' && value !== 'IRX') {
				updatedOptions[index].railCapStyle = '';
			}

			updatedOptions[index][type] = value;
		}

		quoteDispatch({ options: updatedOptions });
	};

	return (
		<Stack
			spacing={2}
			sx={{
				marginBottom: '25px',
				maxWidth: {
					sm: '100%',
					md: '100%',
					lg: '70%',
				},
			}}
		>
			{Array.from({ length: 3 }).map((_, index) => {
				return (
					<Option
						key={index}
						index={index}
						quoteState={quoteState}
						handleOptionSelect={handleOptionSelect}
						drinkRailBrands={drinkRailBrands}
						setShowAddAccessoryModal={setShowAddAccessoryModal}
						setShowDeleteAccessoryModal={setShowDeleteAccessoryModal}
						setPanelKey={setPanelKey}
						accessories={accessories}
						deckingTypeRef={deckingTypeRefs[index]}
						railingTypeRef={railingTypeRefs[index]}
						railCapStyleRef={railCapStyleRefs[index]}
						deckingTypeOpen={deckingTypeOpen[index]}
						railingTypeOpen={railingTypeOpen[index]}
						railCapStyleOpen={railCapStyleOpen[index]}
						setDeckingTypeOpen={(isOpen) => {
							const newOpen = [...deckingTypeOpen];
							newOpen[index] = isOpen;
							setDeckingTypeOpen(newOpen);
						}}
						setRailingTypeOpen={(isOpen) => {
							const newOpen = [...railingTypeOpen];
							newOpen[index] = isOpen;
							setRailingTypeOpen(newOpen);
						}}
						setRailCapStyleOpen={(isOpen) => {
							const newOpen = [...railCapStyleOpen];
							newOpen[index] = isOpen;
							setRailCapStyleOpen(newOpen);
						}}
					/>
				);
			})}

			{showAddAccessoryModal && (
				<AccessorySelection
					key={panelKey}
					accessories={accessories}
					isOpen={showAddAccessoryModal.status}
					optionIndex={showAddAccessoryModal.optionIndex}
					onClose={() =>
						setShowAddAccessoryModal({ status: false, optionIndex: null })
					}
				/>
			)}

			{showDeleteAccessoryModal?.accessory != null &&
				showDeleteAccessoryModal.optionIndex != null && (
					<DeleteAccessoryModal
						modalOpen={showDeleteAccessoryModal.status}
						accessory={showDeleteAccessoryModal.accessory}
						optionIndex={showDeleteAccessoryModal.optionIndex}
						onClose={() => {
							setShowDeleteAccessoryModal({
								status: false,
								accessory: null,
								optionIndex: null,
							});
						}}
					/>
				)}
		</Stack>
	);
};

const Option: React.FC<{
	index: number;
	quoteState: QuoteState;
	handleOptionSelect: (
		type: string,
		value: string | null,
		index: number
	) => void;
	drinkRailBrands: string[];
	setShowAddAccessoryModal: Dispatch<
		SetStateAction<{ status: boolean; optionIndex: number | null }>
	>;
	setShowDeleteAccessoryModal: Dispatch<SetStateAction<any>>;
	setPanelKey: Dispatch<SetStateAction<any>>;
	accessories: Accessory[];
}> = ({
	index,
	quoteState,
	handleOptionSelect,
	drinkRailBrands,
	setShowAddAccessoryModal,
	setShowDeleteAccessoryModal,
	setPanelKey,
	accessories,
}) => {
	const [isOpen, setIsOpen] = useState(index == 0);
	const [selectedAccessory, setSelectedAccessory] = useState<{
		accessory: Accessory | null;
		quantity: number | null;
	}>({ accessory: null, quantity: null });

	const option = quoteState.options[index];

	const deckingTypeValue =
		!option?.deckingType ||
		!quoteState.deckingTypes.includes(option.deckingType)
			? ''
			: option?.deckingType;

	const railingTypeValue =
		!option?.railingType ||
		!quoteState.railingTypes.includes(option.railingType)
			? ''
			: option?.railingType;

	const railCapStyleValue =
		!option?.railCapStyle ||
		![...quoteState.railCapStyles, 'No Drink Rail'].includes(
			option.railCapStyle
		)
			? ''
			: option?.railCapStyle;

	return isOpen ? (
		<Box
			key={index}
			display="flex"
			flexDirection="column"
			alignItems="start"
			gap={2}
			sx={{
				paddingBottom: '30px',
				borderBottom: '1px solid #D4D5D6',
			}}
		>
			<Stack
				sx={{
					width: '100%',
					flexDirection: 'row',
					justifyContent: 'space-between',
					alignItems: 'center',
				}}
			>
				<Typography
					sx={{
						fontWeight: '600',
						color: '#001C41',
						lineHeight: '150%',
						fontStyle: 'normal',
						fontSize: '16px',
						fontFamily: 'inter',
					}}
				>
					Option {index + 1}
				</Typography>

				<IconButton onClick={() => setIsOpen(false)}>
					<KeyboardArrowUpIcon sx={{ color: 'black' }} />
				</IconButton>
			</Stack>

			<Typography
				sx={{
					color: '#001C41',
					lineHeight: '142%',
					fontWeight: 500,
					fontStyle: 'normal',
					fontSize: '14px',
					fontFamily: 'inter',
				}}
			>
				Materials
			</Typography>

			<Stack
				sx={{
					width: '100%',
					display: 'grid',
					gridTemplateColumns: '1fr 1fr',
					gap: '20px',
				}}
			>
				{/* Decking Type Autocomplete */}
				<AppAutocomplete
					id={`decking-type-${index}`}
					options={quoteState.deckingTypes}
					value={deckingTypeValue}
					onChange={(_, value) =>
						handleOptionSelect('deckingType', value, index)
					}
					groupBy={(option: string) => option.split(' ')[0]}
					renderGroup={(params) => (
						<Box key={params.key}>
							{params.key !== '0' && (
								<Divider sx={{ mx: 1, my: 0.5 }} />
							)}
							{params.children}
						</Box>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Decking Type"
							variant="outlined"
							fullWidth
							placeholder="Choose Decking Type"
						/>
					)}
				/>
				{/* Railing Type Autocomplete (disabled if decking type not selected) */}
				<AppAutocomplete
					id={`railing-type-${index}`}
					options={quoteState.railingTypes}
						value={railingTypeValue}
					onChange={(_, value) =>
						handleOptionSelect('railingType', value, index)
					}
					disabled={!deckingTypeValue}
					getOptionDisabled={(option) => {
						const selectedDecking =
							quoteState.options[index]?.deckingType ?? '';
						// Eva-Last brands: disable Colonial
						if (
							option === 'Colonial' &&
							[
								'Eva-Last Infinity',
								'Eva-Last Pioneer',
								'Eva-Last Apex Plus',
							].includes(selectedDecking)
						)
							return true;
						// Duxxbak/Optima: only IRX is supported
						if (
							option !== 'IRX' &&
							['Duxxbak Dekk', 'Optima Dekk'].includes(
								selectedDecking
							)
						)
							return true;
						return false;
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Railing Type"
							variant="outlined"
							fullWidth
							placeholder="Choose Railing Type"
						/>
					)}
				/>
			</Stack>

			<Typography
				sx={{
					color: '#001C41',
					lineHeight: '142%',
					fontWeight: 500,
					fontStyle: 'normal',
					fontSize: '14px',
					fontFamily: 'inter',
				}}
			>
				Additional Railing Configuration
			</Typography>
			<Stack
				sx={{
					width: '100%',
					display: 'grid',
					gridTemplateColumns: '1fr 1fr',
					gap: '20px',
				}}
			>
				{/* Rail Cap Style Autocomplete (disabled if not IRX) */}
				<AppAutocomplete
					id={`rail-cap-style-${index}`}
					options={
						quoteState.options[index]?.railingType?.includes(
							'Timbertech Reliance Core'
						) ||
						quoteState.options[index]?.railingType?.includes(
							'Timbertech Advantage'
						) ||
						quoteState.options[index]?.railingType == 'Fulton' ||
						quoteState.options[index]?.railingType === 'Westbury Tuscany'
							? ['No Drink Rail', 'Drink Rail']
							: quoteState.railCapStyles
					}
					value={railCapStyleValue}
					onChange={(_, value) =>
						handleOptionSelect('railCapStyle', value, index)
					}
					disabled={
						!(
							deckingTypeValue &&
							(quoteState.options[index]?.railingType === 'IRX' ||
								quoteState.options[index]?.railingType?.includes(
									'Timbertech Reliance Core'
								) ||
								quoteState.options[index]?.railingType?.includes(
									'Timbertech Advantage'
								) ||
								quoteState.options[index]?.railingType === 'Fulton' ||
								quoteState.options[index]?.railingType === 'Westbury Tuscany')
						)
					}
					getOptionDisabled={(option) =>
						option === 'Drink Rail' &&
						!drinkRailBrands.includes(deckingTypeValue)
					}
					renderInput={(params) => (
						<TextField
							{...params}
							label="Rail Cap Style"
							variant="outlined"
							fullWidth
						/>
					)}
				/>
			</Stack>

			<Typography
				sx={{
					color: '#001C41',
					lineHeight: '142%',
					fontWeight: 500,
					fontStyle: 'normal',
					fontSize: '14px',
					fontFamily: 'inter',
				}}
			>
				Accessories
			</Typography>

			<Stack
				sx={{
					width: '100%',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'start',
					gap: '20px',
					cursor: 'pointer',
				}}
			>
				<Stack
					sx={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						justifyContent: 'start',
						maxWidth: 'calc(100%-150px)',
						overflowX: 'auto',
						gap: '10px',
					}}
				>
					{option?.accessories.map((accessory) => {
						const accessoryDetails = accessories.find(
							(acc) => acc._id == accessory.id
						);
						if (!accessoryDetails) return <></>;

						return (
							<Box
								key={accessory.id}
								sx={{
									backgroundColor: '#E0F0FF',
									borderRadius: '8px',
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									padding: '5px',
								}}
							>
								<Stack
									sx={{
										display: 'flex',
										flexDirection: 'row',
										justifyContent: 'start',
										alignItems: 'center',
										gap: '5px',
									}}
									onClick={() =>
										setSelectedAccessory({
											accessory: accessoryDetails,
											quantity: accessory.quantity,
											optionIndex: index,
										})
									}
								>
									<Typography
										sx={{
											color: '#0B274D',
											fontSize: '14px',
											fontWeight: '600',
											lineHeight: '14px',
										}}
										noWrap
									>
										{accessoryDetails.name}
									</Typography>

									<Typography sx={{ color: '#64798D' }}>
										({accessory.quantity})
									</Typography>
								</Stack>

								<IconButton
									onClick={() =>
										setShowDeleteAccessoryModal({
											accessory,
											status: true,
											optionIndex: index,
										})
									}
								>
									<CloseIcon sx={{ fontSize: '13px', color: '#64798D' }} />
								</IconButton>
							</Box>
						);
					})}
				</Stack>

				<Button
					startIcon={<AddIcon />}
					sx={{ color: '#001C41', width: '150px' }}
					onClick={() => {
						setShowAddAccessoryModal({ status: true, optionIndex: index });
						setPanelKey(Date.now());
					}}
				>
					Add Accessory
				</Button>
			</Stack>

			{selectedAccessory.accessory && (
				<AccessoryCard
					editMode={true}
					accessory={selectedAccessory.accessory}
					quantity={selectedAccessory.quantity}
					selectedOptions={[index]}
					setSelectedAccessory={setSelectedAccessory}
				/>
			)}
		</Box>
	) : (
		<Stack
			sx={{
				flexDirection: 'row',
				justifyContent: 'space-between',
				borderBottom: '1px solid #D4D5D6',
			}}
		>
			<Typography
				sx={{
					fontWeight: '600',
					color: '#001C41',
					lineHeight: '150%',
					fontStyle: 'normal',
					fontSize: '16px',
					fontFamily: 'inter',
				}}
			>
				Option {index + 1}
			</Typography>

			<IconButton onClick={() => setIsOpen(true)}>
				<ExpandMoreIcon sx={{ color: 'black' }} />
			</IconButton>
		</Stack>
	);
};
